/* useResidentCall — Scan2Call resident-side call lifecycle
   -------------------------------------------------------
   Extracted from `components/doorbell/DoorbellRingDialog.js` (Feb 2026).

   FULL VIDEO CALL (restored Jul 2026). The earlier failures with
   resident video were all in the *incremental publish* path (adding a
   video track mid-call → SDP renegotiation that mobile networks +
   iOS Safari drop silently). The fix is to never renegotiate:

     • Camera + mic are created together and published UPFRONT in a
       single `client.publish([mic, cam])` — the exact same pattern as
       the visitor side, which has always been reliable.
     • Video on/off toggles via `track.setMuted()` — stops frames, no
       renegotiation.
     • Flip camera via `track.setDevice()` — swaps the media source in
       place, no renegotiation.
     • If the camera fails (no device / permission denied) the call
       gracefully continues voice-only instead of aborting.

   The hook owns: join, upfront publish, mute/video toggles, camera
   flip, local preview attach, deterrent playback, WS end-event
   handling, and cleanup.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AgoraRTC from "agora-rtc-sdk-ng";
import { playDeterrentOnCall, DETERRENTS, DEFAULT_DETERRENT_ID } from "../components/doorbell/playDeterrent";
import { useWebSocket } from "../contexts/WebSocketContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_DETERRENT = "guard.scan2call.deterrent";

/**
 * @param {{ ring: object, onEnded?: () => void }} opts
 *   ring = {ring_id, channel, resident_uid, resident_token, agora_app_id, visitor_name, ...}
 *   onEnded fires whenever the call reaches the `ended` stage — the
 *   orchestrator uses it to dismiss the dialog.
 */
export function useResidentCall({ ring, onEnded }) {
  const [stage, setStage] = useState("ringing"); // ringing | connecting | connected | ended
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [facingMode, setFacingMode] = useState("user"); // user | environment
  const [barking, setBarking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Agora network quality — worst of uplink/downlink. 0 unknown,
  // 1 excellent … 6 down. Drives the NetworkBars pill.
  const [netQuality, setNetQuality] = useState(0);
  const [deterrentId, setDeterrentId] = useState(() => {
    try {
      const v = localStorage.getItem(LS_DETERRENT);
      if (v && DETERRENTS.some((d) => d.id === v)) return v;
    } catch { /* noop */ }
    return DEFAULT_DETERRENT_ID;
  });
  const selectedDeterrent = DETERRENTS.find((d) => d.id === deterrentId) || DETERRENTS[0];

  const clientRef = useRef(null);
  const micRef = useRef(null);
  const camRef = useRef(null);
  const remoteContainerRef = useRef(null);
  const remoteVideoTrackRef = useRef(null);
  const localContainerRef = useRef(null);
  // Imperative barking guard so a double-tap can't play two deterrents
  // over each other. The component-facing `barking` state is UI-only.
  const barkingRef = useRef(false);

  // Callback ref for the visitor-video container. The JSX has two
  // possible containers (fullscreen when connected, placeholder while
  // ringing) — a callback ref lets us always play into whichever is
  // currently mounted. Prevents orphaned-<video> bugs from stage
  // transitions.
  const attachRemoteContainer = useCallback((el) => {
    remoteContainerRef.current = el;
    if (el && remoteVideoTrackRef.current) {
      try { remoteVideoTrackRef.current.play(el); } catch { /* noop — track disposed */ }
    }
  }, []);

  // Callback ref for the resident's own self-preview PIP. Same
  // callback-ref pattern (iOS Safari mounts the PIP a beat later than
  // the track exists).
  const attachLocalContainer = useCallback((el) => {
    localContainerRef.current = el;
    if (el && camRef.current) {
      try { camRef.current.play(el); } catch { /* noop — track disposed */ }
    }
  }, []);

  // ---- cleanup --------------------------------------------------------

  const cleanup = useCallback(async () => {
    try { if (micRef.current) { micRef.current.stop(); micRef.current.close(); micRef.current = null; } } catch { /* noop */ }
    try { if (camRef.current) { camRef.current.stop(); camRef.current.close(); camRef.current = null; } } catch { /* noop */ }
    try { if (remoteVideoTrackRef.current) { remoteVideoTrackRef.current.stop(); remoteVideoTrackRef.current = null; } } catch { /* noop */ }
    try { if (clientRef.current) { await clientRef.current.leave(); clientRef.current = null; } } catch { /* noop */ }
  }, []);

  // Backend-initiated end (visitor hangup / wallet drained / orphan
  // reconciler force-end). We don't re-POST /end — backend already did.
  useWebSocket((msg) => {
    if (msg?.type !== "doorbell_end") return;
    if (msg.ring_id !== ring.ring_id) return;
    if (stage === "ended") return;
    if (msg.reason === "wallet_empty") {
      toast.error("Call ended — wallet ran out of credit. Top up to keep using Scan2Call.", { duration: 6000 });
    }
    setStage("ended");
    cleanup();
    onEnded?.();
  });

  // Elapsed-time counter — counts up from 0 once the call connects. No
  // hard cap; backend billing watchdog ends the call via WS when the
  // wallet dries.
  useEffect(() => {
    if (stage !== "connected") return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [stage]);

  // ---- ring lifecycle actions ----------------------------------------

  const handleAnswer = useCallback(async () => {
    setStage("connecting");
    try {
      // Server-side debit happens here.
      await axios.post(`${API}/doorbell/ring/${ring.ring_id}/answer`, {}, { withCredentials: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not answer call");
      setStage("ended");
      onEnded?.();
      return;
    }
    // Validate the WS payload BEFORE touching Agora — a silent "Could
    // not connect" error is almost always missing fields on the ring
    // doc. Turn that into a specific, actionable toast.
    if (!ring.agora_app_id || !ring.channel || !ring.resident_token || !ring.resident_uid) {
      const missing = ["agora_app_id", "channel", "resident_token", "resident_uid"]
        .filter((k) => !ring[k]).join(", ");
      toast.error(`Ring payload incomplete: missing ${missing}. Please try again.`);
      try { await axios.post(`${API}/doorbell/ring/${ring.ring_id}/end`, {}, { withCredentials: true }); } catch { /* best-effort */ }
      setStage("ended");
      onEnded?.();
      return;
    }
    try {
      // codec: "h264" — matches visitor's codec choice for wide mobile
      // compatibility (VP8 has documented negotiation failures on iOS
      // Safari 15+).
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "h264" });
      clientRef.current = client;
      client.on("user-published", async (remoteUser, mediaType) => {
        await client.subscribe(remoteUser, mediaType);
        if (mediaType === "video") {
          remoteVideoTrackRef.current = remoteUser.videoTrack;
          if (remoteContainerRef.current) {
            try { remoteUser.videoTrack.play(remoteContainerRef.current); } catch { /* noop */ }
          }
        }
        if (mediaType === "audio") {
          remoteUser.audioTrack.play();
        }
      });
      client.on("user-unpublished", () => { /* visitor left */ });
      client.on("network-quality", (q) => {
        setNetQuality(Math.max(q?.uplinkNetworkQuality || 0, q?.downlinkNetworkQuality || 0));
      });
      let stepLabel = "join";
      try {
        await client.join(ring.agora_app_id, ring.channel, ring.resident_token, ring.resident_uid);
        stepLabel = "mic-permission";
        // Mic is mandatory; camera is best-effort. If the camera fails
        // (no device / permission), the call continues voice-only
        // rather than aborting — the visitor still hears the resident.
        const mic = await AgoraRTC.createMicrophoneAudioTrack();
        micRef.current = mic;
        stepLabel = "camera";
        let cam = null;
        try {
          cam = await AgoraRTC.createCameraVideoTrack({ facingMode: "user" });
          camRef.current = cam;
          setHasCamera(true);
          if (localContainerRef.current) {
            try { cam.play(localContainerRef.current); } catch { /* noop */ }
          }
        } catch (camErr) {
          toast(`Camera unavailable (${camErr?.message || camErr}) — continuing voice-only.`, { duration: 4000 });
        }
        stepLabel = "publish";
        // ONE upfront publish — never renegotiate mid-call. This is the
        // whole reliability fix (see file header).
        await client.publish(cam ? [mic, cam] : [mic]);
      } catch (agoraErr) {
        const code = agoraErr?.code || agoraErr?.name || "";
        const msg = agoraErr?.message || String(agoraErr);

        console.error(`[Scan2Call] Agora ${stepLabel} failed:`, code, msg, agoraErr);
        throw new Error(`${stepLabel} (${code || "unknown"}): ${msg}`);
      }
      setStage("connected");
    } catch (e) {
      // Agora media session failed. Backend already debited ₦30 on
      // /answer — POSTing /end within REFUND_WINDOW_S refunds it.
      const rawMsg = e?.message || String(e);
      const isPermission = /PERMISSION_DENIED|NotAllowedError|not allowed/i.test(rawMsg);
      const isDevice = /DEVICE_NOT_FOUND|NotFoundError|no microphone|no audio/i.test(rawMsg);
      const isNetwork = /NETWORK|WS_ABORT|CANT_CONNECT|CONNECT_FAILED/i.test(rawMsg);
      let friendly;
      if (isPermission) friendly = "Microphone blocked. Enable mic access in your browser and try again.";
      else if (isDevice) friendly = "No microphone detected on this device.";
      else if (isNetwork) friendly = "Cannot reach Agora servers. Check your internet and try again.";
      else friendly = `Could not connect: ${rawMsg}`;
      toast.error(`${friendly} — refunding your credit.`, { duration: 6000 });
      await cleanup();
      try { await axios.post(`${API}/doorbell/ring/${ring.ring_id}/end`, {}, { withCredentials: true }); } catch { /* best-effort */ }
      setStage("ended");
      onEnded?.();
    }
  }, [ring, cleanup, onEnded]);

  const handleHangup = useCallback(async (reason = "user") => {
    setStage("ended");
    await cleanup();
    try { await axios.post(`${API}/doorbell/ring/${ring.ring_id}/end`, {}, { withCredentials: true }); } catch { /* best-effort */ }
    if (reason === "wallet_empty") toast.error("Call ended — wallet ran out of credit.");
    onEnded?.();
  }, [ring, cleanup, onEnded]);

  const handleDecline = useCallback(async () => {
    setStage("ended");
    try { await axios.post(`${API}/doorbell/ring/${ring.ring_id}/end`, {}, { withCredentials: true }); } catch { /* best-effort */ }
    onEnded?.();
  }, [ring, onEnded]);

  const toggleMute = useCallback(async () => {
    if (!micRef.current) return;
    const next = !muted;
    await micRef.current.setMuted(next);
    setMuted(next);
  }, [muted]);

  // Video on/off — `setMuted` stops sending frames WITHOUT unpublishing,
  // so there's no renegotiation to fail. The visitor sees the standard
  // user-unpublished/user-published events and swaps PIP ↔ voice pill.
  const toggleVideo = useCallback(async () => {
    if (!camRef.current) return;
    const next = !videoOff;
    await camRef.current.setMuted(next);
    setVideoOff(next);
  }, [videoOff]);

  // Flip front ↔ back camera. `setDevice` swaps the capture source on
  // the live track — no unpublish/republish, no renegotiation.
  const flipCamera = useCallback(async () => {
    const cam = camRef.current;
    if (!cam) return;
    const next = facingMode === "user" ? "environment" : "user";
    try {
      const cams = await AgoraRTC.getCameras();
      const wantBack = next === "environment";
      const byLabel = cams.find((d) =>
        (wantBack ? /back|rear|environment/i : /front|user|face/i).test(d.label || "")
      );
      let target = byLabel;
      if (!target && cams.length > 1) {
        const currentId = cam.getMediaStreamTrack?.()?.getSettings?.()?.deviceId;
        target = cams.find((d) => d.deviceId && d.deviceId !== currentId);
      }
      if (!target) throw new Error("no other camera found");
      await cam.setDevice(target.deviceId);
      setFacingMode(next);
    } catch (e) {
      toast.error(`Could not switch camera: ${e?.message || e}`);
    }
  }, [facingMode]);

  const handleBark = useCallback(async () => {
    if (barkingRef.current) return;
    if (stage !== "connected" || !clientRef.current) return;
    barkingRef.current = true;
    try {
      await playDeterrentOnCall({
        deterrentId,
        client: clientRef.current,
        micRef,
        setBarking,
      });
    } catch (e) {
      toast.error(`Deterrent failed: ${e.message || e}`);
      setBarking(false);
    } finally {
      barkingRef.current = false;
    }
  }, [deterrentId, stage]);

  const pickDeterrent = useCallback((id) => {
    setDeterrentId(id);
    try { localStorage.setItem(LS_DETERRENT, id); } catch { /* noop */ }
    const meta = DETERRENTS.find((d) => d.id === id);
    if (meta) toast(`Deterrent: ${meta.label}`, { duration: 1500, icon: "🔊" });
  }, []);

  // Unmount safety.
  useEffect(() => () => { cleanup(); }, [cleanup]);

  return {
    // state
    stage,
    muted,
    videoOff,
    hasCamera,
    facingMode,
    barking,
    elapsed,
    netQuality,
    deterrentId,
    selectedDeterrent,
    // refs — orchestrator attaches these to containers
    attachRemoteContainer,
    attachLocalContainer,
    // actions
    handleAnswer,
    handleHangup,
    handleDecline,
    toggleMute,
    toggleVideo,
    flipCamera,
    handleBark,
    pickDeterrent,
  };
}
